/**
 * -----------------------------------------------------------------------------
 * Popup - Buzzer Alert List
 * -----------------------------------------------------------------------------
 * This popup is used for Buzzer Alert List
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { TModalsState } from "../../api/store/commonSlice";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import { BuzzerAlertStatuses } from "../../api/types/Notifications";
import svgIcons from "../../assets/svg/svgIcons";
import { AdminFormFieldSubmit } from "../../components/admin/formFields";
import FTModal from "../../components/common/FTModal";
import { APP_CONFIG } from "../../constants/constants";
import useBuzzerAlertActions from "../../hooks/buzzerAlertActions";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../utils/dateTime";
import BuzzerAudio from "./buzzerAudio";
import { useAccountTimezone } from "../../hooks/timezone";
import { roundOffLatLong } from "../../utils/map";

const ModalBuzzerAlertList: React.FC = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "buzzerAlert.list",
  });
  const { t: tBuzzerAlert } = useTranslation("translation", {
    keyPrefix: "buzzerAlert",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );

  // retrieve account timezone
  const { tzForDateFns, tzForDisplay } = useAccountTimezone();

  const unreadBuzzerAlerts =
    modalsState?.buzzerAlerts
      ?.filter((alert) =>
        [BuzzerAlertStatuses.PENDING, BuzzerAlertStatuses.INPROGRESS].includes(
          alert.status
        )
      ) // only pending or in-progress alerts are considered unread
      ?.filter(
        (alert) =>
          !modalsState.buzzerAlertsViewed
            .map((alert) => alert.uuid)
            .includes(alert.uuid)
      ) // filter out viewed alerts
      ?.filter(
        (alert) =>
          !modalsState.buzzerAlertsInProgress
            .map((alert) => alert.uuid)
            .includes(alert.uuid)
      ) || []; // filter out alerts that are recently marked as in progress

  const { markViewedInProgressAndNavigateToListing } = useBuzzerAlertActions();
  const viewAlertsCallback = () => {
    markViewedInProgressAndNavigateToListing(unreadBuzzerAlerts);
  };

  if (modalsState?.showBuzzerAlertList === false) return null;

  if (APP_CONFIG.DEBUG.BUZZER) console.error("POPUP AT", new Date());

  return (
    <FTModal
      extraWrapperClass="mt-20"
      isBuzzerAlert={true}
      onHide={() => {}}
      position="top"
      positionHorizontal="right"
      visible={modalsState.showBuzzerAlertList}
    >
      {/* Header */}
      <div className="flex justify-between">
        <h2 className="font-bold text-2xl uppercase text-[#FF0E0E]">
          {t("heading")}
        </h2>
        <div className="font-medium text-xl text-heading-gray444">
          {t("total_alerts", { count: modalsState?.buzzerAlerts?.length || 0 })}
        </div>
      </div>

      {/* List */}
      <div className="grid grid-cols-6 gap-4 mt-6 overflow-y-auto max-h-[calc(100vh-300px)]">
        {unreadBuzzerAlerts?.map((thisBuzzerAlert, index) => (
          <div
            key={index}
            className={`col-span-6 flex${modalsState.buzzerAlertsViewed.map((alert) => alert.uuid).includes(thisBuzzerAlert.uuid) ? " opacity-50" : ""}`}
          >
            <div className="">
              <img src={svgIcons.Hooter} className="size-16" />
            </div>
            <div className="flex-grow flex flex-col ml-8">
              <h4 className="flex justify-between items-start font-bold text-xs uppercase text-[#E23131]">
                <span>{thisBuzzerAlert.rule_description}</span>
                {thisBuzzerAlert.status === BuzzerAlertStatuses.INPROGRESS && (
                  <span className="text-right bg-[#E2313133] font-normal text-[10px] rounded-sm px-2 whitespace-nowrap ms-2">
                    {tBuzzerAlert(`status_${thisBuzzerAlert.status}`)}
                  </span>
                )}
              </h4>
              <div className="text-[10px] mt-1 text-heading-gray888">
                {!!tzForDateFns
                  ? formattedDateTimeBasedOnTimezone(
                      dbTimeToJsTime(parseInt(`${thisBuzzerAlert.timestamp}`)),
                      tzForDateFns,
                      "MM/dd/yyyy - hh:mmaa"
                    )
                  : new Date(
                      parseInt(`${thisBuzzerAlert.timestamp}`)
                    ).toLocaleString()}
                {!!tzForDisplay && ` (${tzForDisplay})`}
              </div>
              <div className="text-xs mt-1 text-heading-gray666 leading-4 text-left">
                <p className="font-bold">
                  {t("asset")}: {thisBuzzerAlert.device_id}
                </p>
                <p>
                  <span className="font-bold">{t("location")}:</span>{" "}
                  {thisBuzzerAlert.event_info?.address || "-"}
                </p>
                <p>
                  <span className="font-bold">{t("latlng")}:</span>{" "}
                  <a
                    className={`underline ${!!thisBuzzerAlert.event_info?.latitude && !!thisBuzzerAlert.event_info?.longitude ? "text-accent-blue-bright" : "text-gray-400 pointer-events-none underline-none"}`}
                    href={APP_CONFIG.LAT_LNG_LINK(
                      thisBuzzerAlert.event_info?.latitude || 0,
                      thisBuzzerAlert.event_info?.longitude || 0
                    )}
                    target="_blank"
                  >{`${roundOffLatLong(thisBuzzerAlert.event_info?.latitude || 0)},${roundOffLatLong(thisBuzzerAlert.event_info?.longitude || 0)}`}</a>
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <AdminFormFieldSubmit
        label={t("button_view_alerts")}
        onClick={viewAlertsCallback}
        type="button"
        variant="primary"
        customWrapperClass="w-full mt-6"
      />

      <BuzzerAudio />
    </FTModal>
  );
};

export default ModalBuzzerAlertList;
